import { faArrowCircleDown, faArrowCircleUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import { notification } from "antd";
import { formatLineReference, formatLineReference2, METRES_PER_CHAIN } from "components/util/distance";
import ELRMileAndChain from "components/util/ELRMileAndChain";
import OBCSpinner from "components/util/OBC";
import { getAbsoluteTimestamp } from "components/util/PlaylistUtils";
import { ReactComponent as OhlSvg } from "icons/ohl.svg";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { customAudit, getNearbyOleStructures } from "redux/actions";
import { roundArrow } from "tippy.js";

const routeIDSelector = (state) => state.playlist.data.routeID;
const playlistSelector = (state) => state.playlist;
const routePositionSelector = (state) => state.playlist.position.routePosition;
const workspaceConfigSelector = (state) => {
    const currentDashboard = _.find(state.dashboards, (dash) => dash.access_token === state.access_token);
    return _.get(currentDashboard, ["config"], {});
};
const userConfigSelector = (state) => state.userDetails.userConfig;

const NearbyOLEStructureRow = ({ item, currentPosition, showDivider }) => {
    const userConfig = useSelector(userConfigSelector);

    const landmarkId = (item.asset?.landmark_id ?? item.asset?.summary_data?.structure ?? "Unknown").replace(/,/g, "/");
    const distanceInMetres = Math.floor(Math.abs(item.asset.position - currentPosition) * METRES_PER_CHAIN);

    const location = ELRMileAndChain.from_fields(
        "ELR Mile & Chain",
        "elr_mile_chain",
        {
            ELR: item.asset.route_id,
            MILE: 0,
            CHAIN: parseInt(item.asset.position),
            TRACK: item.asset.subposition,
        },
        [],
    );

    return (
        <React.Fragment key={item.asset.asset_id}>
            <div className="NearbyOLEAsset">
                <FontAwesomeIcon
                    icon={item.screenDirection === "up" ? faArrowCircleUp : faArrowCircleDown}
                    size="2x"
                    opacity="0.5"
                />
                <div className="AssetInfo">
                    <div className="AssetInfoRow">
                        <span className="NearbyAssetsContentLabel">ID</span>
                        <span className="NearbyAssetsContentValue">{landmarkId}</span>
                    </div>
                    <div className="AssetInfoRow">
                        <span className="NearbyAssetsContentLabel">Distance</span>
                        <span className="NearbyAssetsContentValue">
                            {distanceInMetres} m {item.relation}
                        </span>
                    </div>
                    <div className="AssetInfoRow">
                        <span className="NearbyAssetsContentLabel">Line reference</span>
                        <span className="NearbyAssetsContentValue">{formatLineReference2(userConfig.elr_units, location)}</span>
                    </div>
                </div>
            </div>
            {showDivider && <hr />}
        </React.Fragment>
    );
};

const NearbyOLETooltipContent = ({ playerState, isLoading, hasSearched, nearbyStructures, routePosition }) => {
    if (playerState === "playing") {
        return "Pause video for OLE Structures";
    }

    if (isLoading) {
        return (
            <OBCSpinner
                size={24}
                speed={3}
                color={"#e8dfff"}
            />
        );
    }

    if (!hasSearched) {
        return "Click to search for nearby OLE Structures up to 150m away";
    }

    if (nearbyStructures.length === 0) {
        return <span>No nearby OLE structures found.</span>;
    }

    return (
        <div className="NearbyOLEAssetsContainer">
            {nearbyStructures.map((item, index) => (
                <NearbyOLEStructureRow
                    item={item}
                    currentPosition={routePosition.position}
                    showDivider={index === 0 && nearbyStructures.length > 1}
                />
            ))}
        </div>
    );
};

export const NearbyOLEStructures = () => {
    const dispatch = useDispatch();

    const sessionID = useSelector(routeIDSelector);
    const routePosition = useSelector(routePositionSelector);
    const playlist = useSelector(playlistSelector);
    const workspaceConfig = useSelector(workspaceConfigSelector);

    const [nearbyStructures, setNearbyStructures] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isFeatureEnabled = useMemo(() => {
        return workspaceConfig?.nearby_ole_structures_enabled && workspaceConfig?.asset_groups.includes("2");
    }, [workspaceConfig]);

    useEffect(() => {
        if (hasSearched) {
            setHasSearched(false);
            setNearbyStructures([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routePosition]);

    const auditNearbyStructures = useCallback(() => {
        const { routeID, subposition, position } = routePosition ?? {};

        dispatch(
            customAudit(
                "nearby_ole_structures_click",
                {
                    session: sessionID,
                    timestamp: Math.round(getAbsoluteTimestamp(playlist) * 1000),
                    position: formatLineReference(routeID, subposition, position),
                },
                "User clicked nearby ole structures",
            ),
        );
    }, [dispatch, playlist, routePosition, sessionID]);

    const findNearbyStructures = useCallback(() => {
        if (playlist.position.playerState === "playing") return;
        if (hasSearched || isLoading) return;

        setIsLoading(true);
        setHasSearched(true);

        dispatch(
            getNearbyOleStructures((response) => {
                if (response.success) {
                    setNearbyStructures(response.data);
                } else {
                    notification.error({
                        message: "Error Loading Nearby Structures",
                        description: "Nearby OLE structures could not be retrieved at this time.",
                    });
                }
                setIsLoading(false);
            }),
        );

        auditNearbyStructures();
    }, [auditNearbyStructures, dispatch, hasSearched, isLoading, playlist.position.playerState]);

    if (!isFeatureEnabled) {
        return null;
    }

    return (
        <div>
            <Tippy
                content={
                    <NearbyOLETooltipContent
                        playerState={playlist.position.playerState}
                        isLoading={isLoading}
                        hasSearched={hasSearched}
                        nearbyStructures={nearbyStructures}
                        routePosition={routePosition}
                    />
                }
                className="NearbyOLEAssetsContainerTippy InformationOverlay"
                placement="bottom"
                theme="fullscreen-overlay"
                arrow={roundArrow + roundArrow}
                trigger="mouseenter click"
                appendTo={() => document.getElementById("full-screen-top-layer")}>
                <button
                    onClick={findNearbyStructures}
                    className="OverlayButton with-icon">
                    <OhlSvg className="NearbyOLEIcon" />
                    <span>Nearby OLE Structures</span>
                </button>
            </Tippy>
        </div>
    );
};
